import React, { useState } from 'react';
import { TouchableOpacity, View } from 'react-native';
import { MaterialIcons } from '@expo/vector-icons';
import { useRouter } from 'expo-router';

// React Native Reusables Components
import { Text } from '@/components/ui/text';

// Services & Components
import { intakeResumeAction } from '../../components/onboarding/intakeResume';
import { authAPI, IntakeSubmissionPayload } from '../../services/api';
import type {
  BiggestChallenge,
  CurrentCredential,
  DevelopmentPlan,
  IntakeProfileType,
  IntakeSummary,
  MentorCoachingUse,
  MentorVolume,
} from '../../types';
import OnboardingStepLayout from '../../components/onboarding/OnboardingStepLayout';
import OnboardingLoadingScreen from '../../components/onboarding/OnboardingLoadingScreen';
import OnboardingPlanReveal, {
  type PlanGenerationOutcome,
} from '../../components/onboarding/OnboardingPlanReveal';
import {
  ONBOARDING_COLORS as COLORS,
  ONBOARDING_CONTROL_HEIGHT,
  ONBOARDING_PROGRESS_TRANSITION_MS,
  ONBOARDING_RADII,
  ONBOARDING_SPACING,
  ONBOARDING_TYPOGRAPHY,
} from '../../components/onboarding/onboardingTheme';
import { useTranslation } from '../../hooks/useTranslation';
import { trackAuthEvent } from '../../services/analytics';
import { analyticsErrorCode } from '../../services/analyticsPrivacy';
import { useAuth } from '../../context/AuthContext';
import { resolveIntakeContinueRoute } from '../../utils/intakeContinueRoute';
import { canAccessIndividualPaywall } from '../../utils/roleUtils';

// ───────────────────────────── Domain types ─────────────────────────────

type ProfileType = IntakeProfileType;
type HourBucket = 'under10' | 'tens' | 'fifties' | 'hundreds' | 'master';
type GoalLevel = 'ACC' | 'PCC' | 'MCC' | 'exploring';
type ChallengeOption = 'listening' | 'questions' | 'presence' | 'structure' | 'confidence' | 'other';
type FocusCompetency = 'C4' | 'C5' | 'C6' | 'C7' | 'C8' | 'none' | '';
type EducationOption = 'icf_accredited' | 'coaching_school' | 'self_taught' | 'none' | 'other';
type CredentialOption = CurrentCredential;
type MentorUseOption = MentorCoachingUse;
type MentorVolumeOption = MentorVolume;
type MentorChallengeOption = Extract<
  BiggestChallenge,
  'evidence' | 'calibration' | 'time' | 'difficult_feedback' | 'other'
>;

interface IntakeAnswers {
  profileType: ProfileType | '';
  education: EducationOption | '';
  credential: CredentialOption | '';
  hourBucket: HourBucket | '';
  goalLevel: GoalLevel | '';
  challenge: ChallengeOption | '';
  focusCompetency: FocusCompetency;
  mentorUse: MentorUseOption | '';
  mentorVolume: MentorVolumeOption | '';
  mentorChallenge: MentorChallengeOption | '';
}

// Persisted on the backend as the median of each bucket. The pipeline reads
// `coaching_hours` as an int, so we keep the same field but pick a stable
// representative number per bucket.
const HOURS_BUCKET_TO_VALUE: Record<HourBucket, number> = {
  under10: 5,
  tens: 30,
  fifties: 75,
  hundreds: 250,
  master: 1000,
};
const PLAN_GENERATION_TIMEOUT_MS = 20_000;
// A mentor plan has three practice-level focus areas and runs longer on the model.
const MENTOR_PLAN_GENERATION_TIMEOUT_MS = 45_000;

/** Funnel order marker for analytics: tour → intake → plan selection. */
const FUNNEL_VERSION = 'intake_first';

// Credential-seeking users get one optional competency-focus question. People
// who are still exploring skip the ICF vocabulary until it becomes relevant.
const shouldShowFocusStep = (goal: GoalLevel | ''): boolean => Boolean(goal && goal !== 'exploring');

const getWizardSteps = (profile: ProfileType | '', goal: GoalLevel | ''): StepId[] => {
  // Before a role is chosen, mirror the common branch length so the first
  // screen never renders as a completed one-step wizard.
  if (!profile) return ['profile', 'credential', 'goal', 'hours', 'challenge'];
  if (profile === 'mentor') {
    return ['profile', 'credential', 'mentorUse', 'mentorVolume', 'mentorChallenge', 'menteeFocus'];
  }
  const branch: StepId[] = profile === 'student'
    ? ['profile', 'education', 'goal', 'hours', 'challenge']
    : ['profile', 'credential', 'goal', 'hours', 'challenge'];
  if (shouldShowFocusStep(goal)) branch.push('focus');
  return branch;
};

const GOAL_RANK: Record<Exclude<GoalLevel, 'exploring'>, number> = {
  ACC: 1,
  PCC: 2,
  MCC: 3,
};

const getGoalOptions = (
  profile: ProfileType | '',
  currentCredential: CredentialOption | '',
): GoalLevel[] => {
  const base: GoalLevel[] = profile === 'student'
    ? ['ACC', 'PCC', 'exploring']
    : ['ACC', 'PCC', 'MCC', 'exploring'];
  const currentRank = currentCredential && currentCredential !== 'none' && currentCredential !== 'other'
    ? GOAL_RANK[currentCredential.toUpperCase() as Exclude<GoalLevel, 'exploring'>]
    : 0;
  return base.filter((goal) => goal === 'exploring' || GOAL_RANK[goal] > currentRank);
};

// Wizard step ids. `done` is the terminal results screen.
type StepId =
  | 'profile'
  | 'education'
  | 'credential'
  | 'hours'
  | 'goal'
  | 'challenge'
  | 'focus'
  | 'mentorUse'
  | 'mentorVolume'
  | 'mentorChallenge'
  | 'menteeFocus'
  | 'done';

const asKnownValue = <T extends string>(
  value: string | null | undefined,
  options: readonly T[],
): T | '' => (value && options.includes(value as T) ? value as T : '');

const hourBucketFromValue = (hours: number | null | undefined): HourBucket | '' => {
  if (typeof hours !== 'number' || hours < 0) return '';
  if (hours < 10) return 'under10';
  if (hours < 50) return 'tens';
  if (hours < 100) return 'fifties';
  if (hours < 500) return 'hundreds';
  return 'master';
};

const answersFromSummary = (summary: IntakeSummary): IntakeAnswers => {
  const profileType = asKnownValue(summary.profile_type, ['student', 'coach', 'mentor'] as const);
  const credential = asKnownValue(
    summary.current_credential,
    ['none', 'acc', 'pcc', 'mcc', 'other'] as const,
  );
  const rawGoal = asKnownValue(
    summary.primary_goal,
    ['ACC', 'PCC', 'MCC', 'exploring'] as const,
  );
  const goalLevel = getGoalOptions(profileType, credential).includes(rawGoal as GoalLevel)
    ? rawGoal
    : '';
  const mentorUse = summary.primary_goal?.startsWith('mentor:')
    ? asKnownValue(
      summary.primary_goal.slice('mentor:'.length),
      ['mentee_sessions', 'own_development', 'feedback_prep', 'program'] as const,
    )
    : '';
  const mentorVolume = summary.coaching_frequency?.startsWith('mentor_volume:')
    ? asKnownValue(
      summary.coaching_frequency.slice('mentor_volume:'.length),
      ['none_yet', 'few', 'several', 'many'] as const,
    )
    : '';

  return {
    profileType,
    education: asKnownValue(
      summary.coaching_education,
      ['icf_accredited', 'coaching_school', 'self_taught', 'none', 'other'] as const,
    ),
    credential,
    hourBucket: hourBucketFromValue(summary.coaching_hours),
    goalLevel,
    challenge: asKnownValue(
      summary.biggest_challenge,
      ['listening', 'questions', 'presence', 'structure', 'confidence', 'other'] as const,
    ),
    focusCompetency: asKnownValue(
      summary.focus_competency,
      ['C4', 'C5', 'C6', 'C7', 'C8'] as const,
    ),
    mentorUse,
    mentorVolume,
    mentorChallenge: asKnownValue(
      summary.biggest_challenge,
      ['evidence', 'calibration', 'time', 'difficult_feedback', 'other'] as const,
    ),
  };
};

const isStepAnswered = (step: StepId, answers: IntakeAnswers): boolean => {
  switch (step) {
    case 'profile': return Boolean(answers.profileType);
    case 'education': return Boolean(answers.education);
    case 'credential': return Boolean(answers.credential);
    case 'hours': return Boolean(answers.hourBucket);
    case 'goal': return Boolean(answers.goalLevel);
    case 'challenge': return Boolean(answers.challenge);
    case 'focus':
    case 'menteeFocus': return Boolean(answers.focusCompetency);
    case 'mentorUse': return Boolean(answers.mentorUse);
    case 'mentorVolume': return Boolean(answers.mentorVolume);
    case 'mentorChallenge': return Boolean(answers.mentorChallenge);
    case 'done': return true;
  }
};

interface PostPaymentOnboardingProps {
  onContinue?: () => void;
}

/**
 * Intake wizard — multi-step personalization questions + AI plan reveal.
 *
 * NOTE: despite the legacy file/route name ("post payment"), this screen now
 * runs BEFORE any payment surface: tour → intake → plan reveal → plan
 * selection / checkout → app. Users without billing access continue from the
 * reveal to plan selection; users who already have access go straight to the
 * mentor engagements for entitled mentors, or the first-session upload.
 *
 * One question per page, sharing the OnboardingStepLayout shell so every
 * page in the funnel feels like one designed system. All questions are
 * multi-choice (cleaner LLM signals than free text) except the focus step
 * which only shows for credentialed users targeting a higher credential.
 *
 * We POST to the backend once at the end of the wizard.
 */
export default function PostPaymentOnboardingScreenEnhanced({ onContinue }: PostPaymentOnboardingProps) {
  const router = useRouter();
  const { t, language } = useTranslation();
  const { user, billingStatus } = useAuth();

  // Form state.
  const [profileType, setProfileType] = useState<ProfileType | ''>('');
  const [education, setEducation] = useState<EducationOption | ''>('');
  const [credential, setCredential] = useState<CredentialOption | ''>('');
  const [hourBucket, setHourBucket] = useState<HourBucket | ''>('');
  const [goalLevel, setGoalLevel] = useState<GoalLevel | ''>('');
  const [challenge, setChallenge] = useState<ChallengeOption | ''>('');
  const [focusCompetency, setFocusCompetency] = useState<FocusCompetency>('');
  const [mentorUse, setMentorUse] = useState<MentorUseOption | ''>('');
  const [mentorVolume, setMentorVolume] = useState<MentorVolumeOption | ''>('');
  const [mentorChallenge, setMentorChallenge] = useState<MentorChallengeOption | ''>('');

  // Wizard state.
  type Phase = 'wizard' | 'generating' | 'reveal';
  const [stepIndex, setStepIndex] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [isAdvancing, setIsAdvancing] = useState(false);
  const [isContinuing, setIsContinuing] = useState(false);
  const [isHydrating, setIsHydrating] = useState(true);
  const [phase, setPhase] = useState<Phase>('wizard');
  const [planOutcome, setPlanOutcome] = useState<PlanGenerationOutcome | null>(null);
  const [plan, setPlan] = useState<DevelopmentPlan | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [displayProgress, setDisplayProgress] = useState({ step: 1, total: 5 });

  const submitMutexRef = React.useRef(false);
  const advanceMutexRef = React.useRef(false);
  const continueMutexRef = React.useRef(false);
  const planGenerationMutexRef = React.useRef(false);

  // Profile-first branch list. Later questions change with the selected role,
  // while focus remains conditional for credential-seeking students/coaches.
  const steps: StepId[] = React.useMemo(() => {
    return getWizardSteps(profileType, goalLevel);
  }, [goalLevel, profileType]);

  const totalSteps = steps.length;
  const currentStepId: StepId = phase !== 'wizard' ? 'done' : steps[Math.min(stepIndex, totalSteps - 1)];
  const analyticsStepName: Record<StepId, string> = {
    profile: 'profile',
    education: 'education',
    credential: 'credential',
    hours: 'hours',
    goal: 'goal',
    challenge: 'challenge',
    focus: 'focus',
    mentorUse: 'mentor_use',
    mentorVolume: 'mentor_volume',
    mentorChallenge: 'mentor_challenge',
    menteeFocus: 'mentee_focus',
    done: 'done',
  };
  const currentAnalyticsStep = analyticsStepName[currentStepId];
  const isLastQuestion = stepIndex === totalSteps - 1;
  const stepNumber = displayProgress.step;
  const displayTotalSteps = displayProgress.total;
  const availableGoalLevels = React.useMemo(
    () => getGoalOptions(profileType, credential),
    [credential, profileType],
  );
  const interactionsLocked = submitting || isAdvancing || isHydrating;

  const getAnswersSnapshot = (overrides: Partial<IntakeAnswers> = {}): IntakeAnswers => ({
    profileType,
    education,
    credential,
    hourBucket,
    goalLevel,
    challenge,
    focusCompetency,
    mentorUse,
    mentorVolume,
    mentorChallenge,
    ...overrides,
  });

  // Track time-per-step + total intake duration so we can correlate
  // wording/order changes with completion velocity & abandon points.
  const intakeMountedAtRef = React.useRef<number>(Date.now());
  const activeAnalyticsStepRef = React.useRef({
    id: 'profile',
    index: 1,
    total: 5,
    enteredAt: Date.now(),
  });
  const stepDurationSeconds = (): number => (
    Math.round((Date.now() - activeAnalyticsStepRef.current.enteredAt) / 1000)
  );
  const totalDurationSeconds = (): number => Math.round((Date.now() - intakeMountedAtRef.current) / 1000);

  React.useEffect(() => {
    let cancelled = false;

    void authAPI.getIntakeSummary()
      .then((saved) => {
        if (cancelled) return;
        const restored = answersFromSummary(saved);
        const restoredSteps = getWizardSteps(restored.profileType, restored.goalLevel);
        const firstUnanswered = restoredSteps.findIndex((step) => !isStepAnswered(step, restored));
        const resume = intakeResumeAction(saved, firstUnanswered === -1);
        if (resume === 'reveal_plan') {
          setPlan(saved.development_plan ?? null);
          setPlanOutcome('generated');
          setPhase('reveal');
          return;
        }
        if (resume === 'reveal_skipped') {
          setPlan(null);
          setPlanOutcome('skipped');
          setPhase('reveal');
          return;
        }
        setProfileType(restored.profileType);
        setEducation(restored.education);
        setCredential(restored.credential);
        setHourBucket(restored.hourBucket);
        setGoalLevel(restored.goalLevel);
        setChallenge(restored.challenge);
        setFocusCompetency(restored.focusCompetency);
        setMentorUse(restored.mentorUse);
        setMentorVolume(restored.mentorVolume);
        setMentorChallenge(restored.mentorChallenge);

        if (resume === 'generate_plan') {
          // ONB-25: submitted, plan not persisted yet -> recover/generate it.
          void runPlanGeneration(true);
          return;
        }

        const resumeIndex = firstUnanswered >= 0
          ? firstUnanswered
          : Math.max(0, restoredSteps.length - 1);
        setStepIndex(resumeIndex);
        setDisplayProgress({ step: resumeIndex + 1, total: restoredSteps.length });
      })
      .catch((loadError) => {
        if (cancelled) return;
        console.warn('Failed to restore onboarding intake', loadError);
        setError(t('intake.errors.loadFailed'));
      })
      .finally(() => {
        if (cancelled) return;
        intakeMountedAtRef.current = Date.now();
        setIsHydrating(false);
      });

    return () => {
      cancelled = true;
    };
    // This is a mount-only restore. A language change must not replace edits
    // the user has already made in the current wizard session.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  React.useEffect(() => {
    if (!goalLevel || availableGoalLevels.includes(goalLevel)) return;
    setGoalLevel('');
    setFocusCompetency('');
  }, [availableGoalLevels, goalLevel]);

  const screenViewTrackedRef = React.useRef(false);
  React.useEffect(() => {
    if (isHydrating || screenViewTrackedRef.current) return;
    screenViewTrackedRef.current = true;
    trackAuthEvent('intake_screen_viewed');
  }, [isHydrating]);

  // Phase-transition analytics for the post-wizard surfaces (loading + plan reveal).
  // Fires once per phase entry; lets us measure plan-generation latency from
  // intake_completed → onboarding_plan_reveal_viewed.
  React.useEffect(() => {
    if (phase === 'generating') {
      trackAuthEvent('onboarding_plan_loading_viewed');
    } else if (phase === 'reveal' && planOutcome) {
      trackAuthEvent('onboarding_plan_reveal_viewed', {
        outcome: planOutcome,
        skipped: planOutcome === 'skipped',
        target_level: plan?.target_level || null,
        focus_count: plan?.focus_areas?.length || 0,
        has_plan: planOutcome === 'generated' && Boolean(plan),
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [phase, planOutcome]);

  React.useEffect(() => {
    if (phase !== 'wizard' || isHydrating) return;
    const entered = {
      id: currentAnalyticsStep,
      index: stepNumber,
      total: displayTotalSteps,
      enteredAt: Date.now(),
    };
    activeAnalyticsStepRef.current = entered;
    trackAuthEvent('intake_step_viewed', {
      step_name: entered.id,
      step_index: entered.index,
      total_steps: entered.total,
    });
    // The logical step id is the event identity. Branch-length changes while
    // the same question is visible must not restart its timer or double-count.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStepId, isHydrating, phase]);

  // ───────────────────────── Wizard navigation ─────────────────────────

  const releaseAdvanceMutex = () => {
    advanceMutexRef.current = false;
    setIsAdvancing(false);
  };

  const advance = (overrides: Partial<IntakeAnswers> = {}) => {
    if (advanceMutexRef.current || submitMutexRef.current || isHydrating) return;
    advanceMutexRef.current = true;
    setIsAdvancing(true);
    const analyticsStep = activeAnalyticsStepRef.current;
    trackAuthEvent('intake_advance_clicked', {
      step_name: analyticsStep.id,
      step_index: analyticsStep.index,
      time_on_step_seconds: stepDurationSeconds(),
      is_last_question: isLastQuestion,
    });
    if (isLastQuestion) {
      void handleSubmit(overrides).finally(releaseAdvanceMutex);
    } else {
      const nextAnswers = getAnswersSnapshot(overrides);
      const nextSteps = getWizardSteps(nextAnswers.profileType, nextAnswers.goalLevel);
      const logicalIndex = Math.max(0, nextSteps.indexOf(currentStepId));
      const nextIndex = Math.min(nextSteps.length - 1, logicalIndex + 1);
      setStepIndex(nextIndex);
      setDisplayProgress({ step: nextIndex + 1, total: nextSteps.length });
      setTimeout(releaseAdvanceMutex, ONBOARDING_PROGRESS_TRANSITION_MS);
    }
  };

  const goBack = () => {
    if (stepIndex === 0 || interactionsLocked || advanceMutexRef.current) return;
    const analyticsStep = activeAnalyticsStepRef.current;
    trackAuthEvent('intake_back_clicked', {
      from_step_name: analyticsStep.id,
      from_step_index: analyticsStep.index,
      time_on_step_seconds: stepDurationSeconds(),
    });
    const previousIndex = Math.max(0, stepIndex - 1);
    setStepIndex(previousIndex);
    setDisplayProgress({ step: previousIndex + 1, total: steps.length });
  };

  // ───────────────────────── Submit / skip ─────────────────────────

  // Map the multi-choice goal level into the existing free-text primary_goal
  // field so the backend doesn't need a migration to receive it.
  const goalToPrimaryGoal = (level: GoalLevel | ''): string | undefined => {
    if (!level) return undefined;
    if (level === 'exploring') return 'exploring';
    return level; // 'ACC' | 'PCC' | 'MCC'
  };

  // Each field has its own backend column. Building from a supplied snapshot
  // is essential on the final skip action, where React state has not committed
  // the cleared answer yet.
  const buildPayload = (answers: IntakeAnswers): IntakeSubmissionPayload => {
    const focusEligible = answers.profileType !== 'mentor' && shouldShowFocusStep(answers.goalLevel);
    const isMentor = answers.profileType === 'mentor';
    return {
      profile_type: answers.profileType || undefined,
      coaching_education: answers.profileType === 'student' && answers.education
        ? answers.education
        : undefined,
      current_credential: answers.profileType !== 'student' && answers.credential
        ? answers.credential
        : undefined,
      coaching_hours: !isMentor && answers.hourBucket
        ? HOURS_BUCKET_TO_VALUE[answers.hourBucket]
        : undefined,
      primary_goal: isMentor
        ? answers.mentorUse ? `mentor:${answers.mentorUse}` : undefined
        : goalToPrimaryGoal(answers.goalLevel),
      coaching_frequency: isMentor && answers.mentorVolume
        ? `mentor_volume:${answers.mentorVolume}`
        : undefined,
      focus_competency: (isMentor || focusEligible)
        && answers.focusCompetency
        && answers.focusCompetency !== 'none'
        ? answers.focusCompetency
        : undefined,
      biggest_challenge: isMentor
        ? answers.mentorChallenge || undefined
        : answers.challenge || undefined,
      language: language || 'en',
      generate_suggestions: false,
    };
  };

  const runPlanGeneration = async (refetchLatestFirst: boolean) => {
    if (planGenerationMutexRef.current) return;
    planGenerationMutexRef.current = true;
    setError(null);
    setPlanOutcome(null);
    setPhase('generating');

    try {
      if (refetchLatestFirst) {
        try {
          const latest = await authAPI.getIntakeSummary();
          if (latest.development_plan) {
            setPlan(latest.development_plan);
            setPlanOutcome('generated');
            trackAuthEvent('development_plan_recovered', {
              target_level: latest.development_plan.target_level,
              focus_count: latest.development_plan.focus_areas.length,
            });
            return;
          }
        } catch (refreshError) {
          // A failed status refresh should not consume the retry action. The
          // idempotent generation endpoint remains safe to call afterward.
          console.warn('Failed to refresh latest development plan before retry', refreshError);
        }
      }

      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const withPlan = await Promise.race([
          authAPI.generateDevelopmentPlan(language as 'en' | 'tr' | undefined),
          new Promise<never>((_, reject) => {
            timeoutId = setTimeout(
              () => reject(new Error('development_plan_timeout')),
              profileType === 'mentor' ? MENTOR_PLAN_GENERATION_TIMEOUT_MS : PLAN_GENERATION_TIMEOUT_MS,
            );
          }),
        ]);
        if (!withPlan.development_plan) throw new Error('development_plan_missing');
        setPlan(withPlan.development_plan);
        setPlanOutcome('generated');
        trackAuthEvent('development_plan_generated', {
          target_level: withPlan.development_plan.target_level,
          focus_count: withPlan.development_plan.focus_areas.length,
        });
      } finally {
        if (timeoutId) clearTimeout(timeoutId);
      }
    } catch (planError: any) {
      console.warn('Plan generation failed', planError);
      setPlan(null);
      setPlanOutcome('failed');
      trackAuthEvent('development_plan_failed', {
        error_code: analyticsErrorCode(planError),
        http_status: planError?.response?.status ?? null,
      });
    } finally {
      planGenerationMutexRef.current = false;
      setPhase('reveal');
    }
  };

  const handleSubmit = async (overrides: Partial<IntakeAnswers> = {}) => {
    if (submitMutexRef.current) return;
    submitMutexRef.current = true;
    setSubmitting(true);

    try {
      setError(null);
      const answers = getAnswersSnapshot(overrides);
      if (!answers.profileType) {
        const profileIndex = Math.max(0, steps.indexOf('profile'));
        trackAuthEvent('intake_validation_error', {
          field: 'profile_type',
          step_name: activeAnalyticsStepRef.current.id,
          step_index: activeAnalyticsStepRef.current.index,
        });
        setError(t('intake.errors.profileTypeRequired'));
        setStepIndex(profileIndex);
        setDisplayProgress({ step: profileIndex + 1, total: steps.length });
        return;
      }

      const payload = buildPayload(answers);
      await authAPI.submitIntake(payload);
      trackAuthEvent('intake_completed', {
        profile_type: answers.profileType,
        profile_branch: answers.profileType,
        education: answers.education || 'none',
        current_credential: answers.credential || 'none',
        mentor_use: answers.mentorUse || 'none',
        mentor_volume: answers.mentorVolume || 'none',
        mentor_challenge: answers.mentorChallenge || 'none',
        hour_bucket: answers.hourBucket,
        goal: answers.goalLevel,
        challenge: answers.challenge,
        focus_eligible: shouldShowFocusStep(answers.goalLevel),
        focus_competency: answers.focusCompetency || 'none',
        total_duration_seconds: totalDurationSeconds(),
        total_steps: getWizardSteps(answers.profileType, answers.goalLevel).length,
        funnel_version: FUNNEL_VERSION,
      });

      await runPlanGeneration(false);
    } catch (submitError) {
      console.warn('Failed to save onboarding intake', submitError);
      setError(t('intake.errors.submitFailed'));
      setPhase('wizard');
    } finally {
      submitMutexRef.current = false;
      setSubmitting(false);
    }
  };

  const handleSkipAll = async () => {
    if (submitMutexRef.current || advanceMutexRef.current || isHydrating) return;
    submitMutexRef.current = true;
    setSubmitting(true);

    try {
      setError(null);
      await authAPI.skipIntake();
      setPlan(null);
      setPlanOutcome('skipped');
      setPhase('reveal');
      trackAuthEvent('intake_skipped', {
        step_name: activeAnalyticsStepRef.current.id,
        step_index: activeAnalyticsStepRef.current.index,
        total_duration_seconds: totalDurationSeconds(),
      });
    } catch (skipError) {
      console.warn('Failed to skip onboarding intake', skipError);
      setError(t('intake.errors.skipFailed'));
    } finally {
      submitMutexRef.current = false;
      setSubmitting(false);
    }
  };

  const handleRetryPlan = () => {
    if (planGenerationMutexRef.current || submitMutexRef.current) return;
    void runPlanGeneration(true);
  };

  const {
    hasBillingAccess,
    goesToPlanSelection,
    goesToMentorEngagements,
    route: continueRoute,
  } = resolveIntakeContinueRoute({
    billingStatus,
    userType: user?.user_type,
    canAccessIndividualPaywall: Boolean(user && canAccessIndividualPaywall(user as any)),
    profileType,
    planKind: plan?.plan_kind,
    hasPlan: !!plan,
  });

  // The reveal CTA names its real destination now that payment follows intake.
  const revealContinueLabel = goesToPlanSelection
    ? t('intake.revealCtaPlans')
    : goesToMentorEngagements
      ? t('intake.revealCtaEngagements')
      : undefined;

  const handleContinueToApp = () => {
    if (continueMutexRef.current) return;
    continueMutexRef.current = true;
    setIsContinuing(true);
    trackAuthEvent('intake_continue_to_app_clicked', {
      from_phase: planOutcome || phase,
      has_access: hasBillingAccess,
      destination: goesToPlanSelection
        ? 'plan_selection'
        : goesToMentorEngagements ? 'mentor_engagements' : 'first_session_upload',
      total_duration_seconds: totalDurationSeconds(),
      had_plan: !!plan,
      funnel_version: FUNNEL_VERSION,
    });

    void (async () => {
      try {
        await onContinue?.();
        router.replace(
          continueRoute as any
        );
      } catch (continueError) {
        console.warn('Failed to continue from onboarding plan reveal', continueError);
        continueMutexRef.current = false;
        setIsContinuing(false);
      }
    })();
  };

  // ───────────────────────── Shared bits ─────────────────────────

  const errorBanner = error ? (
    <View
      className="flex-row items-center"
      style={{
        gap: ONBOARDING_SPACING.sm,
        minHeight: ONBOARDING_CONTROL_HEIGHT,
        padding: ONBOARDING_SPACING.md,
        borderRadius: ONBOARDING_RADII.sm,
        backgroundColor: COLORS.errorSubtle,
        borderColor: COLORS.error,
        borderWidth: 1,
      }}
      accessibilityRole="alert"
      accessibilityLiveRegion="assertive"
      accessibilityLabel={error}
    >
      <MaterialIcons name="error-outline" size={20} color={COLORS.error} />
      <Text style={{ ...ONBOARDING_TYPOGRAPHY.bodySmall, color: COLORS.error, flex: 1 }}>
        {error}
      </Text>
    </View>
  ) : null;

  const continueLabel = isLastQuestion ? t('intake.submit') : t('intake.next');
  const skipQuestionCta = (
    overrides: Partial<IntakeAnswers>,
    resetCurrent: () => void,
  ) => ({
    label: t('intake.skipQuestion'),
    onPress: () => {
      if (advanceMutexRef.current || submitMutexRef.current || isHydrating) return;
      const analyticsStep = activeAnalyticsStepRef.current;
      trackAuthEvent('intake_question_skipped', {
        step_name: analyticsStep.id,
        step_index: analyticsStep.index,
        time_on_step_seconds: stepDurationSeconds(),
      });
      resetCurrent();
      advance(overrides);
    },
    disabled: interactionsLocked,
  });

  // ─────────────────── Terminal phases (post-wizard) ───────────────────

  if (phase === 'generating') {
    // Mentor plans are written by the model too, so mentors see the real "building your plan" step.
    return <OnboardingLoadingScreen onComplete={() => { /* state advances on its own */ }} />;
  }

  if (phase === 'reveal' && planOutcome) {
    return (
      <OnboardingPlanReveal
        plan={plan}
        profileType={profileType}
        outcome={planOutcome}
        onContinue={handleContinueToApp}
        onRetry={handleRetryPlan}
        continuing={isContinuing}
        continueLabel={revealContinueLabel}
        opensMentees={goesToMentorEngagements}
      />
    );
  }

  // ───────────────────────── Wizard steps ─────────────────────────

  if (currentStepId === 'profile') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="person-outline"
        title={t('intake.questions.profileType')}
        primaryCta={{
          label: continueLabel,
          onPress: advance,
          disabled: !profileType || interactionsLocked,
          loading: submitting && isLastQuestion,
        }}
        secondaryCta={{ label: t('intake.skip'), onPress: handleSkipAll, disabled: interactionsLocked }}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.md }}>
          {(['student', 'coach', 'mentor'] as const).map((type) => (
            <RadioCard
              key={type}
              selected={profileType === type}
              title={t(`intake.profileTypes.${type}`)}
              description={t(`intake.profileTypes.${type}Description`)}
              onPress={() => {
                if (profileType !== type) {
                  setEducation('');
                  setCredential('');
                  setHourBucket('');
                  setGoalLevel('');
                  setChallenge('');
                  setFocusCompetency('');
                  setMentorUse('');
                  setMentorVolume('');
                  setMentorChallenge('');
                  setStepIndex(0);
                }
                setProfileType(type);
                trackAuthEvent('intake_step_completed', { step_name: 'profile_type', value: type });
              }}
              disabled={interactionsLocked}
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'education') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="school"
        title={t('intake.questions.education')}
        primaryCta={{ label: continueLabel, onPress: advance, disabled: !education || interactionsLocked }}
        secondaryCta={skipQuestionCta({ education: '' }, () => setEducation(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['icf_accredited', 'coaching_school', 'self_taught', 'none', 'other'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={education === opt}
              title={t(`intake.educationOptions.${opt}.title`)}
              description={t(`intake.educationOptions.${opt}.description`)}
              onPress={() => {
                setEducation(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'education', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'credential') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="workspace-premium"
        title={t('intake.questions.credential')}
        primaryCta={{ label: continueLabel, onPress: advance, disabled: !credential || interactionsLocked }}
        secondaryCta={skipQuestionCta({ credential: '' }, () => setCredential(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['none', 'acc', 'pcc', 'mcc', 'other'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={credential === opt}
              title={t(`intake.credentialOptions.${opt}.title`)}
              description={t(`intake.credentialOptions.${opt}.description`)}
              onPress={() => {
                setCredential(opt);
                if (!getGoalOptions(profileType, opt).includes(goalLevel as GoalLevel)) {
                  setGoalLevel('');
                  setFocusCompetency('');
                }
                trackAuthEvent('intake_step_completed', { step_name: 'credential', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'hours') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="timer"
        title={t('intake.questions.hours')}
        primaryCta={{
          label: continueLabel,
          onPress: advance,
          disabled: !hourBucket || interactionsLocked,
          loading: submitting && isLastQuestion,
        }}
        secondaryCta={skipQuestionCta({ hourBucket: '' }, () => setHourBucket(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['under10', 'tens', 'fifties', 'hundreds', 'master'] as const).map((bucket) => (
            <RadioCard
              key={bucket}
              selected={hourBucket === bucket}
              title={t(`intake.hourBuckets.${bucket}.title`)}
              description={t(`intake.hourBuckets.${bucket}.description`)}
              onPress={() => {
                setHourBucket(bucket);
                trackAuthEvent('intake_step_completed', { step_name: 'hours', value: bucket });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'goal') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="flag"
        title={t('intake.questions.goal')}
        subtitle={t('intake.questions.goalHelper')}
        primaryCta={{
          label: continueLabel,
          onPress: advance,
          disabled: !goalLevel || interactionsLocked,
          loading: submitting && isLastQuestion,
        }}
        secondaryCta={skipQuestionCta(
          { goalLevel: '', focusCompetency: '' },
          () => {
            setGoalLevel('');
            setFocusCompetency('');
          },
        )}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {availableGoalLevels.map((opt) => (
            <RadioCard
              key={opt}
              selected={goalLevel === opt}
              title={t(`intake.goalLevels.${opt}.title`)}
              description={t(`intake.goalLevels.${opt}.description`)}
              onPress={() => {
                setGoalLevel(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'goal', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'challenge') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="psychology"
        title={t('intake.questions.challenge')}
        primaryCta={{
          label: continueLabel,
          onPress: advance,
          disabled: !challenge || interactionsLocked,
          loading: submitting && isLastQuestion,
        }}
        secondaryCta={skipQuestionCta({ challenge: '' }, () => setChallenge(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['listening', 'questions', 'presence', 'structure', 'confidence', 'other'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={challenge === opt}
              title={t(`intake.challengeOptions.${opt}.title`)}
              description={t(`intake.challengeOptions.${opt}.description`)}
              onPress={() => {
                setChallenge(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'challenge', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'mentorUse') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="supervisor-account"
        title={t('intake.questions.mentorUse')}
        primaryCta={{ label: continueLabel, onPress: advance, disabled: !mentorUse || interactionsLocked }}
        secondaryCta={skipQuestionCta({ mentorUse: '' }, () => setMentorUse(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['mentee_sessions', 'own_development', 'feedback_prep', 'program'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={mentorUse === opt}
              title={t(`intake.mentorUseOptions.${opt}.title`)}
              description={t(`intake.mentorUseOptions.${opt}.description`)}
              onPress={() => {
                setMentorUse(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'mentor_use', value: opt });
              }}
              disabled={interactionsLocked}
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'mentorVolume') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="groups"
        title={t('intake.questions.mentorVolume')}
        primaryCta={{ label: continueLabel, onPress: advance, disabled: !mentorVolume || interactionsLocked }}
        secondaryCta={skipQuestionCta({ mentorVolume: '' }, () => setMentorVolume(''))}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['none_yet', 'few', 'several', 'many'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={mentorVolume === opt}
              title={t(`intake.mentorVolumeOptions.${opt}.title`)}
              description={t(`intake.mentorVolumeOptions.${opt}.description`)}
              onPress={() => {
                setMentorVolume(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'mentor_volume', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'mentorChallenge') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="psychology"
        title={t('intake.questions.mentorChallenge')}
        primaryCta={{ label: continueLabel, onPress: advance, disabled: !mentorChallenge || interactionsLocked }}
        secondaryCta={skipQuestionCta(
          { mentorChallenge: '' },
          () => setMentorChallenge(''),
        )}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['evidence', 'calibration', 'time', 'difficult_feedback', 'other'] as const).map((opt) => (
            <RadioCard
              key={opt}
              selected={mentorChallenge === opt}
              title={t(`intake.mentorChallengeOptions.${opt}.title`)}
              description={t(`intake.mentorChallengeOptions.${opt}.description`)}
              onPress={() => {
                setMentorChallenge(opt);
                trackAuthEvent('intake_step_completed', { step_name: 'mentor_challenge', value: opt });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
        </View>
      </OnboardingStepLayout>
    );
  }

  if (currentStepId === 'menteeFocus' || currentStepId === 'focus') {
    return (
      <OnboardingStepLayout
        step={stepNumber}
        totalSteps={displayTotalSteps}
        journeyPart="intake"
        helperText={t('intake.helperText')}
        icon="auto-awesome"
        title={t(currentStepId === 'menteeFocus' ? 'intake.questions.menteeFocus' : 'intake.questions.focusCompetency')}
        subtitle={t(currentStepId === 'menteeFocus' ? 'intake.questions.menteeFocusHelper' : 'intake.questions.focusCompetencyHelper')}
        primaryCta={{
          label: continueLabel,
          onPress: advance,
          disabled: interactionsLocked,
          loading: submitting,
        }}
        onBack={goBack}
      >
        {errorBanner}
        <View style={{ gap: ONBOARDING_SPACING.sm }}>
          {(['C6', 'C7', 'C5', 'C4', 'C8'] as const).map((code) => (
            <RadioCard
              key={code}
              selected={focusCompetency === code}
              title={t(`intake.focusCompetencies.${code}.title`)}
              description={t(`intake.focusCompetencies.${code}.description`)}
              onPress={() => {
                setFocusCompetency(code);
                trackAuthEvent('intake_step_completed', { step_name: currentStepId === 'menteeFocus' ? 'mentee_focus' : 'focus_competency', value: code });
              }}
              disabled={interactionsLocked}
              compact
            />
          ))}
          <RadioCard
            selected={focusCompetency === 'none'}
            title={t('intake.focusCompetencies.none.title')}
            description={t('intake.focusCompetencies.none.description')}
            onPress={() => {
              setFocusCompetency('none');
              trackAuthEvent('intake_step_completed', { step_name: currentStepId === 'menteeFocus' ? 'mentee_focus' : 'focus_competency', value: 'none' });
            }}
            disabled={interactionsLocked}
            compact
          />
        </View>
      </OnboardingStepLayout>
    );
  }

  return null;
}

// ────────────────────────────────────────────────────────────────────────────
// Local primitives
// ────────────────────────────────────────────────────────────────────────────

interface RadioCardProps {
  selected: boolean;
  title: string;
  description?: string;
  onPress: () => void;
  disabled?: boolean;
  compact?: boolean;
}

function RadioCard({ selected, title, description, onPress, disabled, compact }: RadioCardProps) {
  return (
    <TouchableOpacity
      onPress={onPress}
      disabled={disabled}
      className="flex-row items-center"
      style={{
        gap: ONBOARDING_SPACING.md,
        minHeight: ONBOARDING_CONTROL_HEIGHT,
        padding: compact ? ONBOARDING_SPACING.md : ONBOARDING_SPACING.lg,
        borderRadius: ONBOARDING_RADII.sm,
        backgroundColor: disabled
          ? COLORS.surfaceDisabled
          : selected
            ? COLORS.primarySubtle
            : COLORS.surface,
        borderWidth: 2,
        borderColor: selected && !disabled ? COLORS.primary : COLORS.border,
      }}
      accessibilityRole="radio"
      accessibilityLabel={[title, description].filter(Boolean).join('. ')}
      accessibilityState={{ selected, disabled: Boolean(disabled) }}
    >
      <View style={{ flex: 1, minWidth: 0 }}>
        <Text
          style={{
            ...(compact ? ONBOARDING_TYPOGRAPHY.bodySmall : ONBOARDING_TYPOGRAPHY.body),
            color: disabled ? COLORS.text.disabled : COLORS.text.primary,
            fontWeight: '600',
          }}
        >
          {title}
        </Text>
        {description ? (
          <Text
            style={{
              ...ONBOARDING_TYPOGRAPHY.bodySmall,
              color: disabled ? COLORS.text.disabled : COLORS.text.secondary,
              marginTop: ONBOARDING_SPACING.xs,
            }}
          >
            {description}
          </Text>
        ) : null}
      </View>
      {selected ? (
        <MaterialIcons
          name="check-circle"
          size={ONBOARDING_SPACING.xl}
          color={disabled ? COLORS.text.disabled : COLORS.primary}
        />
      ) : null}
    </TouchableOpacity>
  );
}
